export interface ServiceStatus {
  running: boolean;
  version?: string;
  devices?: number;
  connectedDevices?: number;
  timestamp?: number;
}

export class ServiceManagerService {
  private serviceUrl = 'http://127.0.0.1:28473';
  private healthCheckInterval: number | null = null;

  /**
   * Проверяет статус сервиса Device Bridge
   * Работает как для установленного системного сервиса, так и для локально запущенного в режиме разработки
   * (например, через `npm run dev` в device-bridge-service)
   * 
   * @returns Promise<ServiceStatus> Статус сервиса
   */
  async checkServiceStatus(): Promise<ServiceStatus> {
    try {
      const response = await fetch(`${this.serviceUrl}/api/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(2000) // 2 second timeout
      });

      if (response.ok) {
        const headerVersion = response.headers.get('X-Service-Version') || undefined;
        let data;
        try {
          data = await response.json();
        } catch (jsonError) {
          // If we can't parse JSON but got 200, service is probably running
          // Don't log - this is not a critical error
          return { 
            running: true, 
            version: headerVersion,
            timestamp: Date.now()
          };
        }
        
        // Get version from JSON response if available, otherwise use header
        const version = (data.version && data.version.trim()) || (headerVersion && headerVersion.trim()) || undefined;
        
        // Get detailed status
        try {
          const statusResponse = await fetch(`${this.serviceUrl}/api/status`, {
            method: 'GET',
            signal: AbortSignal.timeout(2000)
          });
          
          if (statusResponse.ok) {
            const status = await statusResponse.json();
            return {
              running: true,
              version: (status.version && status.version.trim()) || version,
              devices: status.devices,
              connectedDevices: status.connectedDevices,
              timestamp: status.timestamp
            };
          } else {
            // Health check passed, but status endpoint failed
            // Still consider service as running
            // Don't log - this is not a critical error
            return { 
              running: true, 
              version: version || data.version,
              timestamp: data.timestamp || Date.now()
            };
          }
        } catch (statusError: any) {
          // Health check passed, but status endpoint failed
          // Still consider service as running
          // Don't log - this is not a critical error
          return { 
            running: true, 
            version: version || data.version,
            timestamp: data.timestamp || Date.now()
          };
        }
      } else {
        // Service returned non-OK status - this is normal if service is not running
        // Don't log
        return { running: false };
      }
    } catch (error: any) {
      // Service is not running or not accessible
      // This includes:
      // - Service not installed
      // - Service not started
      // - Service running in dev mode but not started
      // - Network/firewall issues
      // - CORS issues
      // This is a normal situation - don't log as error to avoid spam
      // Only log if it's an unexpected error type
      const errorMessage = error.message || String(error);
      if (!errorMessage.includes('Failed to fetch') && 
          !errorMessage.includes('Load failed') && 
          !errorMessage.includes('network') &&
          !errorMessage.includes('timeout')) {
        // Unexpected error - log it
        console.warn('Service health check failed with unexpected error:', errorMessage);
      }
      return { running: false };
    }
  }

  async startService(): Promise<void> {
    try {
      const response = await fetch(`${this.serviceUrl}/api/service/start`, {
        method: 'POST'
      });
      if (!response.ok) {
        throw new Error('Не удалось запустить сервис');
      }
    } catch (error: any) {
      if (error.message && error.message.includes('Не удалось')) {
        throw error;
      }
      throw new Error('Сервис не запущен. Пожалуйста, установите и запустите сервис.');
    }
  }

  async stopService(): Promise<void> {
    try {
      const response = await fetch(`${this.serviceUrl}/api/service/stop`, {
        method: 'POST'
      });
      if (!response.ok) {
        throw new Error('Не удалось остановить сервис');
      }
    } catch (error: any) {
      if (error.message && error.message.includes('Не удалось')) {
        throw error;
      }
      throw new Error('Не удалось связаться с сервисом. Убедитесь, что сервис запущен.');
    }
  }

  async restartService(): Promise<void> {
    try {
      const response = await fetch(`${this.serviceUrl}/api/service/restart`, {
        method: 'POST'
      });
      if (!response.ok) {
        throw new Error('Не удалось перезапустить сервис');
      }
    } catch (error: any) {
      if (error.message && error.message.includes('Не удалось')) {
        throw error;
      }
      throw new Error('Не удалось связаться с сервисом. Убедитесь, что сервис запущен.');
    }
  }

  async getDevices(): Promise<any[]> {
    try {
      const response = await fetch(`${this.serviceUrl}/api/devices`, {
        signal: AbortSignal.timeout(2000)
      });
      if (!response.ok) {
        throw new Error('Не удалось получить список устройств');
      }
      const data = await response.json();
      return data.devices || [];
    } catch (error: any) {
      if (error.message && error.message.includes('Не удалось')) {
        throw error;
      }
      throw new Error('Не удалось получить список устройств. Сервис может быть не запущен.');
    }
  }

  startHealthCheck(callback: (status: ServiceStatus) => void, interval = 5000): void {
    this.stopHealthCheck();
    let consecutiveFailures = 0;
    let currentInterval = interval;
    
    const checkStatus = async () => {
      const status = await this.checkServiceStatus();
      callback(status);
      
      // If service is not running, increase interval to reduce spam
      // After 3 consecutive failures, check less frequently (every 30 seconds)
      if (!status.running) {
        consecutiveFailures++;
        if (consecutiveFailures >= 3 && currentInterval < 30000) {
          // Increase interval to reduce spam
          this.stopHealthCheck();
          currentInterval = 30000; // 30 seconds
          this.healthCheckInterval = window.setInterval(checkStatus, currentInterval);
        }
      } else {
        consecutiveFailures = 0;
        // If service is running, use normal interval
        if (currentInterval > interval) {
          this.stopHealthCheck();
          currentInterval = interval;
          this.healthCheckInterval = window.setInterval(checkStatus, currentInterval);
        }
      }
    };
    
    this.healthCheckInterval = window.setInterval(checkStatus, currentInterval);
  }

  stopHealthCheck(): void {
    if (this.healthCheckInterval !== null) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  getServiceUrl(): string {
    return this.serviceUrl;
  }

  getWebSocketUrl(): string {
    return this.serviceUrl.replace('http://', 'ws://');
  }
}

export const serviceManager = new ServiceManagerService();

